import {
  getIdToken,
  getAuth,
  signInWithEmailAndPassword,
  User,
  Auth,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import ClientAppError from './utils/clientAppError';
import { auth } from './firebase/firebaseInit';

// Map Firebase auth error codes to user-friendly messages
export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/too-many-requests': 'Too many requests. Try again later.',
  'auth/invalid-credential': 'Invalid username or password.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Invalid username or password.',
  'auth/invalid-email': 'Invalid email format.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
};

export interface AuthenticationResult {
  user: User | null;
  userRole: string | null;
  error?: ClientAppError;
}

export async function extractUserRole(user: User): Promise<string> {
  const idTokenResult = await user.getIdTokenResult();
  const userRole = idTokenResult.claims.role ? String(idTokenResult.claims.role) : null;

  if (!userRole) {
    throw ClientAppError.authError('User role not found in token claims');
  }

  return userRole;
}

export async function loginWithEmailAndPassword(
  email: string,
  password: string,
  authInstance: Auth = auth,
): Promise<AuthenticationResult> {
  try {
    if (!email || !password) {
      throw ClientAppError.invalidInput('Email and password are required');
    }

    const userCredential = await signInWithEmailAndPassword(authInstance, email, password);
    const { user } = userCredential;

    if (!user) {
      throw ClientAppError.authError('Authentication failed: No user returned');
    }

    try {
      const userRole = await extractUserRole(user);

      return { user, userRole };
    } catch (error) {
      return {
        user: null,
        userRole: null,
        error: error instanceof ClientAppError ? error : ClientAppError.fromError(error, 'Failed to get user role'),
      };
    }
  } catch (error) {
    if (error instanceof FirebaseError) {
      const errorMessage = AUTH_ERROR_MESSAGES[error.code] || `Authentication error: ${error.message}`;
      return {
        user: null,
        userRole: null,
        error: ClientAppError.authError(errorMessage),
      };
    }

    return {
      user: null,
      userRole: null,
      error: error instanceof ClientAppError
        ? error
        : ClientAppError.fromError(error, 'Login failed'),
    };
  }
}

export async function getFirebaseIdToken(authInstance?: Auth) {
  const currentAuth = authInstance || getAuth();
  if (!currentAuth.currentUser) {
    throw ClientAppError.fromError(
      new Error('User is not authenticated so cannot get token'),
    );
  }
  const { currentUser } = currentAuth;
  const idToken = await getIdToken(currentUser);
  return idToken;
}
